import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { GoalReviewTypes } from "@utils/constants";
import React, { useEffect, useRef, useState } from "react";

import { Label } from "@fronterahealth/frontera-ui-components";

import { CATALIGHT_GOAL_STATUS_SELECT_OPTIONS } from "@components/ShortTermGoalPanel/catalightConstants";
import { GoalsFilterCategoryRow } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalsFilterCategoryRow";
import { GoalsFilterDateRangeSubPanel } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalsFilterDateRangeSubPanel";

export interface DateRangeFilter {
  preset: string | null;
  startDate: string | null;
  endDate: string | null;
}

const EMPTY_DATE_RANGE: DateRangeFilter = { preset: null, startDate: null, endDate: null };

export interface GoalsFilterValues {
  status: string[];
  reviewed: string[];
  dateRange: DateRangeFilter;
}

export const EMPTY_FILTER_VALUES: GoalsFilterValues = {
  status: [],
  reviewed: [],
  dateRange: EMPTY_DATE_RANGE,
};

export const hasActiveFilters = (filters: GoalsFilterValues): boolean =>
  filters.status.length > 0 || filters.reviewed.length > 0 || filters.dateRange.preset !== null;

type ActiveCategory = "status" | "reviewed" | "dateRange" | null;

interface GoalsFilterPopoverProps {
  filters: GoalsFilterValues;
  onFiltersChange: (filters: GoalsFilterValues) => void;
  onClose: () => void;
  setExpandedAll: (expanded: boolean) => void;
}

export const GoalsFilterPopover: React.FC<GoalsFilterPopoverProps> = ({
  filters,
  onFiltersChange,
  onClose,
  setExpandedAll,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [activeCategory, setActiveCategory] = useState<ActiveCategory>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleClearAll = () => {
    onFiltersChange(EMPTY_FILTER_VALUES);
    setActiveCategory(null);
  };

  const toggleOption = (key: "status" | "reviewed", id: string) => {
    const current = filters[key];
    const updated = current.includes(id) ? current.filter((s) => s !== id) : [...current, id];

    if (key === "reviewed") {
      setExpandedAll(updated.includes(GoalReviewTypes["Not Approved"]));
    }
    onFiltersChange({ ...filters, [key]: updated });
  };

  return (
    <div
      ref={popoverRef}
      className="absolute left-0 top-full z-50 mt-1 w-[220px] rounded-lg border border-limestone-100 bg-white pb-2 pt-4 shadow-[0px_6px_12px_0px_rgba(0,0,0,0.12)]"
    >
      <div className="pb-1 pl-3">
        <Label size="extra-small" displayType="loud" colorType="primary">
          Filters
        </Label>
      </div>

      <GoalsFilterCategoryRow
        category={{ key: "status", label: "Goal Status", options: CATALIGHT_GOAL_STATUS_SELECT_OPTIONS }}
        isActive={activeCategory === "status"}
        selected={filters.status}
        onToggleActive={() => setActiveCategory(activeCategory === "status" ? null : "status")}
        onToggle={(id) => toggleOption("status", id)}
        onClear={() => onFiltersChange({ ...filters, status: [] })}
      />

      {/* Date Range */}
      <div className="relative px-2 py-1">
        <button
          className={`flex w-full flex-1 items-center justify-between rounded p-1 hover:bg-limestone-50 ${
            activeCategory === "dateRange" ? "bg-limestone-50" : ""
          }`}
          onClick={() => setActiveCategory(activeCategory === "dateRange" ? null : "dateRange")}
        >
          <span className="text-sm font-normal leading-[22px] text-limestone-900">Date Range</span>
          <div className="flex items-center gap-1">
            {filters.dateRange.preset !== null && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-green-100 px-1.5 text-xs font-semibold text-green-700">
                1
              </span>
            )}
            <ChevronRightIcon className="h-4 w-4 text-limestone-600" />
          </div>
        </button>
        {activeCategory === "dateRange" && (
          <GoalsFilterDateRangeSubPanel
            value={filters.dateRange}
            onChange={(dateRange) => onFiltersChange({ ...filters, dateRange })}
            onClear={() => onFiltersChange({ ...filters, dateRange: EMPTY_DATE_RANGE })}
          />
        )}
      </div>

      <GoalsFilterCategoryRow
        category={{
          key: "not approved",
          label: "Approval State",
          options: [
            {
              id: GoalReviewTypes["Not Approved"],
              label: "Show Unapproved Only",
            },
          ],
        }}
        isActive={activeCategory === "reviewed"}
        selected={filters.reviewed}
        onToggleActive={() => setActiveCategory(activeCategory === "reviewed" ? null : "reviewed")}
        onToggle={(id) => {
          toggleOption("reviewed", id);
        }}
        onClear={() => onFiltersChange({ ...filters, reviewed: [] })}
      />

      {hasActiveFilters(filters) && (
        <div className="flex flex-col gap-2 px-4 pt-1">
          <div className="h-px w-full bg-limestone-200" />
          <button
            className="text-left text-sm font-semibold leading-[22px] text-green-700 hover:text-green-800"
            onClick={handleClearAll}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};
